import FilesList from 'components/v2/file-row/FilesList';
import { ExpandableHelpHtml } from 'components/v2/help-boxes/ExpandableHelpHtml';
import React, { useState } from 'react';
import useProperty from '../hooks/useProperty';
import usePropertyPath from '../hooks/usePropertyPath';
import { DoubleFieldPanel, FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import CompilerSelectControlledComponent from 'components/v2/epd-compiler-inputs/CompilerSelectControlledComponent';
import { useEffect } from 'react';
import LanguagesService from 'services/LanguagesService';
import { Option } from 'types/types';
import { LanguageModel } from 'services/EpdClient';
import { createFilter } from 'react-select';
import FileUpload from 'components/v2/file-upload/FileUpload';
import { ButtonUploadIcon } from 'components/v2/icons';
import { deleteUploadFile, postUploadFile } from 'services/api/dynamicFormsFilesApi';
import { MAX_IMG_SIZE } from 'constants/constants';
import { toaster } from 'components/v2/toast';
import { t } from 'i18next';
import { ChangeEvent } from 'react';
import { useDocumentStore } from '../hooks/useDocumentStore';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { getFirstOrDefault } from '../utils/getFirstOrDefault';
import useDataSourceById from '../hooks/useDataSourceById';
import { DocumentDataSourceOption } from 'types/dynamic-forms/DocumentDataSourceOption';
import { LcaXLValidationErrorCode } from 'types/lca-results/LcaXLValidationErrorCode';
import { LCAResultsOutdatedTemplateDialog } from 'components/v2/dialogs/LCAResultsOutdatedTemplateDialog';

type OptionWithCode = Option & { code?: string };
type DocumentTypeOption = DocumentDataSourceOption;

const filterConfig = {
  ignoreCase: true,
  ignoreAccents: true,
  trim: true,
  matchFrom: 'start' as const,
};

const FilesListRenderer: React.FC<any> = ({
  propertyId,
  siteHeadingText,
  helpText,
  label,
  tooltip,
  metadataIsRequired,
  supportedFileExtensions,
  uploadFileType,
}) => {
  const store = useDocumentStore();
  const { docId } = store;
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, isVisible, isDisabled, errorMessage } = useProperty<Record<string, any>[]>(propertyPath);
  const nestedErrorMessage = getFirstOrDefault(store.getErrorMessagesIncludingNested(propertyPath));
  const displayError = errorMessage ?? nestedErrorMessage;
  const [documentType, setDocumentType] = useState<DocumentTypeOption>();
  const [documentLanguage, setDocumentLanguage] = useState<OptionWithCode>();
  const [languageList, setLanguages] = useState<LanguageModel[]>([]);
  const { options: documentTypeOptions } = useDataSourceById('documentTypes');
  const effectiveMetadataIsRequired = typeof metadataIsRequired === 'boolean' ? metadataIsRequired : true;
  const normalizedExtensions = Array.isArray(supportedFileExtensions)
    ? supportedFileExtensions
        .map((x: string) => x?.toString().trim().toLowerCase().replace(/^\./, ''))
        .filter((x: string) => x.length > 0)
    : ['pdf'];
  const acceptedExtensions = normalizedExtensions.map((x: string) => `.${x}`).join(',');
  const canUploadDocument =
    !effectiveMetadataIsRequired || (Boolean(documentType?.value) && Boolean(documentLanguage?.code));
  const headingText = siteHeadingText ?? label;
  const renderedHelpText = (helpText ?? tooltip ?? '').toString();
  const uploadPropertyName = (uploadFileType ?? 'Document').toString();
  const isRequired = store.getIsRequired(propertyPath);
  const isRemovalForced = store.getIsRemovalForced(propertyPath);

  const getLcaErrorDetails = (code?: string): string => {
    switch (code) {
      case LcaXLValidationErrorCode.INVALID_TEMPLATE:
        return 'Ensure that the file is created using the latest version of the template and try uploading it again.';
      case LcaXLValidationErrorCode.INVALID_MODULE_VALUE:
      case LcaXLValidationErrorCode.INVALID_MODULE_COLUMNS:
      case LcaXLValidationErrorCode.INVALID_VALUE_MAX_LENGTH:
        return 'Please review the instructions, update the file, and try again.';
      default:
        return 'Please try again or contact support if the issue persists.';
    }
  };

  useEffect(() => {
    const fetchLanguages = async () => {
      const result = await LanguagesService.getLanguages();
      setLanguages(result);
    };

    fetchLanguages();
  }, []);

  if (!isVisible) {
    return null;
  }

  const handleRemove = (id: string) => {
    void (async () => {
      try {
        await deleteUploadFile(docId, id, propertyId);
        setValue(value.filter((x) => x.id !== id));
      } catch {
        toaster({
          severity: 'error',
          summary: 'Error',
          details: 'Could not remove the file. Please try again.',
        });
      }
    })();
  };

  const uploadFile = async (docId: string, propertyName: string, file: File | undefined) => {
    if (!file) {
      return null;
    }
    if (!canUploadDocument) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: 'Please select Document type and Document language before uploading.',
      });
      return;
    }
    if (file.size > MAX_IMG_SIZE) {
      toaster({
        severity: 'error',
        summary: 'Error',
        details: t('epdWizard.messages.errorTooLargeImageFile', {
          size: Math.floor(MAX_IMG_SIZE / (1024 * 1024)),
        }) as string,
      });

      return;
    }

    const result = await postUploadFile(docId, {
      propertyName: propertyName,
      propertyId,
      docId: docId,
      file: file,
      documentTypeId: effectiveMetadataIsRequired ? documentType?.value ?? null : null,
      documentLanguageCode: effectiveMetadataIsRequired ? documentLanguage?.code ?? null : null,
    });
    if (result.isSuccessful) {
      const nextFileItem: Record<string, any> = {
        id: result.model.fileId,
        name: result.model.fileName || file.name,
      };
      if (effectiveMetadataIsRequired && documentType?.meta?.shortName) {
        nextFileItem.documentTypeShortName = documentType.meta.shortName;
      }

      setValue([...value, nextFileItem]);
    } else {
      const lcaErrorCode = result.model?.lcaValidationErrorCode;
      if (lcaErrorCode === LcaXLValidationErrorCode.OUTDATED_TEMPLATE) {
        LCAResultsOutdatedTemplateDialog();
      } else if (lcaErrorCode) {
        toaster({
          severity: 'error',
          summary: 'The file couldn’t be processed.',
          details: getLcaErrorDetails(lcaErrorCode),
        });
      } else {
        toaster({
          severity: 'error',
          summary: 'Error',
          details: result.message || 'Could not upload the file. Please try again.',
        });
      }
    }

    // @ts-ignore
    setDocumentType(null);
    // @ts-ignore
    setDocumentLanguage(null);
  };

  return (
    <>
      <FieldPanelFullWidth style={{ gridRowGap: 0, padding: 0 }}>
        <SmartLabel
          label={headingText}
          required={isRequired}
          hasValue={value && value.length > 0}
          error={displayError}
        />
        {!isDisabled && (
          <>
            <ExpandableHelpHtml html={renderedHelpText} />
            {effectiveMetadataIsRequired && (
              <DoubleFieldPanel className="mt-5">
                <CompilerSelectControlledComponent
                  label="Document type"
                  tooltip="EPD related documents."
                  name="docType"
                  options={documentTypeOptions}
                  value={documentType}
                  onChanged={(name: string, value: string) => {
                    if (!value) {
                      setDocumentType(undefined);
                      return;
                    }
                    setDocumentType(documentTypeOptions.find((x) => x.value == value) ?? { value, label: value });
                  }}
                  isClearable={true}
                />
                <CompilerSelectControlledComponent
                  label="Document language"
                  tooltip="Specify the language of the provided document."
                  name="docLang"
                  options={languageList?.map((item) => {
                    return {
                      value: item.id,
                      label: item.name,
                      code: item.code,
                    } as OptionWithCode;
                  })}
                  value={documentLanguage}
                  onChanged={(name: string, value: string) => {
                    setDocumentLanguage({
                      value: value,
                      label: languageList.find((x) => x.id == value)?.name || value,
                      code: languageList.find((x) => x.id == value)?.code,
                    });
                  }}
                  isClearable={false}
                  filterOption={createFilter(filterConfig)}
                />
              </DoubleFieldPanel>
            )}
            <FileUpload
              label="Upload document"
              name={uploadPropertyName}
              icon={ButtonUploadIcon}
              accept={acceptedExtensions}
              validExtensions={normalizedExtensions}
              disabled={!canUploadDocument}
              onFileUpload={(e: ChangeEvent<HTMLInputElement>, propertyName: string) =>
                uploadFile(docId, propertyName, e.target.files?.[0] ?? undefined)
              }
            />
          </>
        )}
        <FilesList
          files={(value ?? []).map((fileItem) => ({
            ...fileItem,
            isRemovable: isRemovalForced ? true : fileItem.isRemovable ?? !isDisabled,
          }))}
          disabled={isDisabled}
          onRemoveFile={isDisabled ? undefined : handleRemove}
        />
      </FieldPanelFullWidth>
    </>
  );
};

export default FilesListRenderer;
